import React, { useState } from 'react';
import {
  Badge,
  Button,
  Center,
  Container,
  Flex,
  Box,
  Text,
  AlertIcon,
  Alert,
  CircularProgress,
  CircularProgressLabel,
  SimpleGrid,
  Tag,
} from '@chakra-ui/react';
import { FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { formatDateFull } from '../../utils';
import {
  countVariantOccupiedSeats,
  isVariantOpenToGuests,
} from '../../utils/guestEventEligibility';
import EmbeddedVariants from './EmbeddedVariants';

const GuestLink = ({ onClick }) => (
  <Text fontSize="sm" color="gray.600" mt={2}>
    oppure{' '}
    <Button variant="link" colorScheme="blue" size="sm" onClick={onClick}>
      partecipa come ospite
    </Button>
  </Text>
);

const EventVariantsVersion6 = ({
  readOnly = false,
  event,
  handleOpenDrawer,
  onQuickSignup,
  userData,
  isSoldOut,
  allParticipantsPaidInFull,
  allVarianti = null,
  registrationOpen = undefined,
  isEarlyAccessOnly = false,
  embedded = false,
  // Presente quando chi guarda può prenotare come ospite (non tesserato)
  onGuestBooking,
}) => {
  const guestMode = typeof onGuestBooking === 'function';
  // Retrocompatibilità: se registrationOpen non passato, usa event.iscrizioniAperte (boolean o string)
  const open =
    registrationOpen !== undefined
      ? registrationOpen
      : event?.iscrizioniAperte === true || event?.iscrizioniAperte === 'true';
  // Usa allVarianti se fornite, altrimenti usa le varianti dell'evento originali
  let { Varianti } = event;
  Varianti = (allVarianti || Varianti)
    ?.slice()
    .sort((a, b) => new Date(a.Data_inizio) - new Date(b.Data_inizio));

  const [expanded, setExpanded] = useState(false);

  // Helper per verificare se l'utente ha almeno un tesseramento valido per l'anno corrente
  const hasValidTesseramentoForCurrentYear = () => {
    if (!userData?.tesseramenti || userData.tesseramenti.length === 0) {
      return false;
    }

    const currentYear = new Date().getFullYear();

    return userData.tesseramenti.some((socio) => {
      const tesseramentiSocio =
        socio.tesseramenti && Array.isArray(socio.tesseramenti)
          ? socio.tesseramenti
          : [];

      return tesseramentiSocio.some(
        (t) =>
          t.anno === currentYear &&
          (t.paymentStatus === 'succeeded' || t.paymentStatus === 'pending')
      );
    });
  };

  // embedded: dentro la card "Iscrizioni" della pagina evento (sfondo bianco, bottoni blu)
  const Wrapper = embedded ? Box : Container;
  const wrapperProps = embedded
    ? {}
    : {
        maxW: 'container.lg',
        mt: 8,
        p: 8,
        borderRadius: 'xl',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 0 24px 4px rgba(0,0,0,0.5)',
      };
  const ctaScheme = embedded ? 'blue' : 'yellow';

  return (
    <Wrapper {...wrapperProps}>
      {embedded ? (
        <EmbeddedVariants
          varianti={Varianti}
          event={event}
          canClick={Boolean(
            open &&
              !event.externalURL &&
              (guestMode || (!allParticipantsPaidInFull && userData))
          )}
          onSelect={handleOpenDrawer}
          isSelectable={
            guestMode ? (v) => isVariantOpenToGuests(v, event) : undefined
          }
        />
      ) : (
        /* Variants container with collapsible behavior on mobile */
        <Box
          position="relative"
          overflow="hidden"
          // On mobile, if not expanded, restrict height (approx. 1.5 cards).
          maxHeight={{
            base: expanded ? 'none' : '210px',
            md: 'none',
          }}
        >
          <SimpleGrid gap={4} columns={{ base: 1, md: 3 }}>
            {Varianti?.map((variant, index) => {
              const dataInizio = formatDateFull(variant.Data_inizio, {
                withYear: false,
              });
              const dataFine = variant.Data_Fine
                ? formatDateFull(variant.Data_Fine, {
                    withYear: false,
                  })
                : '';
              const postiDisponibili = variant.Capienza
                ? variant.Capienza - countVariantOccupiedSeats(variant)
                : 0;
              const progressValue = variant.Capienza
                ? postiDisponibili / variant.Capienza
                : 0;
              const percentage = Math.round(progressValue * 100);

              const soldOut = postiDisponibili <= 0;

              const canClick =
                !allParticipantsPaidInFull &&
                open &&
                userData &&
                !event.externalURL;

              return (
                <Flex
                  key={variant.id}
                  borderRadius="md"
                  boxShadow="sm"
                  bg="green.50"
                  alignItems={soldOut ? 'flex-start' : 'center'}
                  p={soldOut ? 2 : 4}
                  align="center"
                  justify="space-between"
                  position="relative"
                  _active={{
                    transform: canClick && !soldOut ? 'scale(0.98)' : '',
                  }}
                  _hover={{
                    bg: canClick && !soldOut ? 'blue.200' : '',
                  }}
                  cursor={canClick && !soldOut ? 'pointer' : 'default'}
                  onClick={() => {
                    if (canClick) {
                      if (!soldOut) {
                        handleOpenDrawer(variant);
                      }
                    }
                  }}
                  // On mobile when collapsed, apply a fade-out mask to the second (partially visible) card.
                  sx={
                    !expanded && index === 1 && window.innerWidth < 600
                      ? {
                          WebkitMaskImage:
                            'linear-gradient(to bottom, #000 -100%,transparent 80%, transparent 100%)',
                          maskImage:
                            'linear-gradient(to bottom, #000 -100%,transparent 80%, transparent 100%)',
                        }
                      : {}
                  }
                >
                  <Box>
                    <Text
                      fontSize={soldOut ? 'sm' : 'lg'}
                      fontWeight="bold"
                      color="gray.700"
                    >
                      {variant.Nome || 'Variante senza nome'}
                    </Text>
                    <Badge
                      bg="green.100"
                      mt={soldOut ? 1 : 2}
                      mb={soldOut ? 8 : 0}
                      p={1}
                      px={2}
                      borderRadius="sm"
                      display="flex"
                      alignItems="center"
                      color="gray.700"
                    >
                      <FaCalendarAlt style={{ marginRight: '4px' }} />
                      {dataInizio} {dataFine ? ` - ${dataFine}` : ''}
                    </Badge>
                  </Box>
                  {Boolean(variant.Capienza) &&
                    (!soldOut ? (
                      <CircularProgress
                        right={3}
                        top="21px"
                        position="absolute"
                        value={percentage}
                        size="52px"
                        color="yellow.400"
                      >
                        <CircularProgressLabel
                          fontSize="lg"
                          color="gray.700"
                          fontWeight="bold"
                        >
                          {postiDisponibili}
                        </CircularProgressLabel>
                      </CircularProgress>
                    ) : (
                      <Tag
                        colorScheme="red"
                        fontWeight="bold"
                        textAlign="center"
                        size="sm"
                        pt={1.5}
                        pr={3.5}
                        pl={3.5}
                        pb={1.5}
                        position="absolute"
                        borderBottomLeftRadius={0}
                        borderBottomRightRadius={0}
                        borderTopLeftRadius="md"
                        borderTopRightRadius="md"
                        bottom={0}
                        right="50%"
                        width="auto"
                        whiteSpace="nowrap"
                        transform="translateX(50%)"
                        transformOrigin="50%"
                      >
                        Posti Esauriti
                      </Tag>
                    ))}
                </Flex>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* "Mostra di piu" button - only on mobile when list is collapsed */}
      {!expanded && !embedded && (
        <Center
          mt={8}
          mb={embedded ? 4 : 24}
          display={{ base: 'flex', md: 'none' }}
        >
          <Button
            size="lg"
            variant="link"
            color={embedded ? 'blue.500' : 'blue.300'}
            onClick={() => setExpanded(true)}
            _active={{
              transform: 'scale(0.98)',
            }}
          >
            Mostra di più
          </Button>
        </Center>
      )}

      {/* Bottone Prenota */}
      {!readOnly && (
        <Center mt={embedded ? 6 : 16}>
          {userData ? (
            allParticipantsPaidInFull ? (
              <Alert status="success" borderRadius="md" maxW="300px">
                <AlertIcon />
                Sei già iscritto a questo evento!
              </Alert>
            ) : (
              <Flex alignItems="center" flexDirection="column">
                {!hasValidTesseramentoForCurrentYear() ? (
                  <>
                    <Button
                      as={Link}
                      to="/account/tesseramenti"
                      colorScheme="blue"
                      size="lg"
                      minW="200px"
                    >
                      Rinnova Tessera
                    </Button>
                    {guestMode ? (
                      <GuestLink onClick={() => onGuestBooking()} />
                    ) : null}
                  </>
                ) : event.externalURL ? (
                  <Button
                    colorScheme="purple"
                    size="lg"
                    as={Link}
                    minW="200px"
                    to={event.externalURL}
                    mt={2}
                    target="_blank"
                    isDisabled={!open}
                    data-umami-event="external_booking_click"
                    data-umami-event-event_id={event.documentId}
                  >
                    Partecipa
                  </Button>
                ) : (
                  <Button
                    alignItems="center"
                    colorScheme={ctaScheme}
                    minW="200px"
                    size="lg"
                    onClick={handleOpenDrawer}
                    isDisabled={isSoldOut || !open}
                  >
                    {isSoldOut
                      ? 'Evento Sold Out'
                      : isEarlyAccessOnly
                        ? 'ISCRIVITI (Early access)'
                        : 'ISCRIVITI'}
                  </Button>
                )}
                {!open && hasValidTesseramentoForCurrentYear() && (
                  <Badge
                    bg="yellow.600"
                    color="white"
                    fontSize="lg"
                    px={4}
                    py={1}
                    mt={4}
                    borderRadius={0}
                    textTransform="default"
                  >
                    Le iscrizioni apriranno a breve! ☝️
                  </Badge>
                )}
              </Flex>
            )
          ) : event.externalURL ? (
            <Button
              colorScheme={ctaScheme}
              size="lg"
              as={Link}
              minW="200px"
              to={event.externalURL}
              mt={2}
              target="_blank"
              isDisabled={!open}
              data-umami-event="external_booking_click"
              data-umami-event-event_id={event.documentId}
            >
              Partecipa
            </Button>
          ) : onQuickSignup ? (
            <Flex flexDirection="column" alignItems="center" gap={1}>
              <Button
                colorScheme={ctaScheme}
                size="lg"
                minW="200px"
                onClick={onQuickSignup}
              >
                Tesserati e iscriviti
              </Button>
              {guestMode ? (
                <GuestLink onClick={() => onGuestBooking()} />
              ) : null}
            </Flex>
          ) : guestMode ? (
            <Flex flexDirection="column" alignItems="center" gap={1}>
              <Button
                colorScheme={ctaScheme}
                size="lg"
                minW="200px"
                onClick={() => onGuestBooking()}
                isDisabled={isSoldOut}
                data-testid="event-guest-variant-button"
              >
                Partecipa come ospite
              </Button>
              <Text fontSize="sm" color="gray.600">
                oppure{' '}
                <Button
                  as={Link}
                  to="/tesseramento"
                  variant="link"
                  colorScheme="blue"
                  size="sm"
                >
                  diventa socio
                </Button>
              </Text>
            </Flex>
          ) : (
            <Button
              as={Link}
              to={isEarlyAccessOnly && !open ? '/sostienicil' : '/tesseramento'}
              colorScheme={ctaScheme}
              size="lg"
              isDisabled={isSoldOut}
            >
              {isEarlyAccessOnly && !open
                ? "Sostieni CIL per l'early access"
                : 'Diventa Socio'}
            </Button>
          )}
        </Center>
      )}
    </Wrapper>
  );
};

export default EventVariantsVersion6;
